/**
 * ТЗ 0106, этап 3 — доска договоров (`/contracts`, с ТЗ 0120 — вид по умолчанию): колонки по
 * статусам Documenso, без перетаскивания (статус ведёт Documenso), и
 * плитки-итоги над списком. Модуль без React, Apollo и i18n.
 */

import moment from 'moment';

import { warsawDaysAgoIso } from 'core/utils/zoomTime';

import { esignStatusTone, EsignTagTone } from './esignModel';

/**
 * Вид раздела: ТЗ 0120 — по умолчанию доска; список — `?view=list`.
 * Последний выбор помнится в этом браузере, адрес главнее (старые ссылки
 * `?view=board` по-прежнему открывают доску).
 */
export const CONTRACTS_VIEW_PARAM = 'view';
export const CONTRACTS_VIEWS = ['board', 'list'] as const;
export type ContractsView = typeof CONTRACTS_VIEWS[number];

const CONTRACTS_VIEW_STORAGE_KEY = 'erp.contracts.view';

const isContractsView = (value: unknown): value is ContractsView =>
  value === 'board' || value === 'list';

export const readStoredContractsView = (): ContractsView | null => {
  try {
    const value = window.localStorage.getItem(CONTRACTS_VIEW_STORAGE_KEY);
    return isContractsView(value) ? value : null;
  } catch (error) {
    return null;
  }
};

export const storeContractsView = (view: ContractsView): void => {
  try {
    window.localStorage.setItem(CONTRACTS_VIEW_STORAGE_KEY, view);
  } catch (error) {
    // Хранилище закрыто — вид просто не запомнится.
  }
};

/** Вид: адрес главнее, затем запомненный, по умолчанию — доска. */
export const parseContractsView = (
  search: string,
  stored: ContractsView | null = null,
): ContractsView => {
  const value = new URLSearchParams(search).get(CONTRACTS_VIEW_PARAM);
  if (isContractsView(value)) return value;
  return stored ?? 'board';
};

/** Адрес с видом: доска — без параметра (адрес по умолчанию не растёт). */
export const withContractsView = (
  search: string,
  view: ContractsView,
): string => {
  const params = new URLSearchParams(search);
  if (view === 'list') params.set(CONTRACTS_VIEW_PARAM, 'list');
  else params.delete(CONTRACTS_VIEW_PARAM);
  return params.toString();
};

/**
 * Колонки доски: черновик, «ошибка отправки» (красная — договор не ушёл и
 * ждёт человека), отправлен (и в пути), открыт, подписан, «не случился»
 * (отклонён, истёк, отменён). Каждый статус — ровно в одной колонке.
 */
export const CONTRACT_BOARD_COLUMNS = [
  { key: 'draft', statuses: ['draft'] },
  { key: 'failed', statuses: ['send_failed'] },
  { key: 'sent', statuses: ['sending', 'sent'] },
  { key: 'opened', statuses: ['opened'] },
  { key: 'completed', statuses: ['completed'] },
  { key: 'closed', statuses: ['rejected', 'expired', 'cancelled'] },
] as const;

export type ContractBoardColumnKey = typeof CONTRACT_BOARD_COLUMNS[number]['key'];

export interface IContractBoardColumn {
  key: ContractBoardColumnKey;
  /** Статусы колонки с учётом выбранных в фильтре. */
  statuses: string[];
}

/**
 * Колонки под фильтром статусов: выбраны статусы — колонка берёт только их,
 * колонка без выбранных — не показывается (как «выбор этапа = только он»).
 * «Ошибка отправки» — только когда такие договоры есть (по счётчикам
 * статусов) или статус выбран явно: пустая красная колонка — лишний шум.
 */
export const contractBoardColumns = (
  selected: ReadonlyArray<string>,
  counts?: Readonly<Record<string, number>>,
): IContractBoardColumn[] =>
  CONTRACT_BOARD_COLUMNS.map((column) => ({
    key: column.key,
    statuses: (column.statuses as readonly string[]).filter(
      (status) => !selected.length || selected.includes(status),
    ),
  })).filter((column) => {
    if (!column.statuses.length) return false;
    if (column.key !== 'failed' || selected.length) return true;
    const known = counts && Object.keys(counts).length > 0;
    return !known || (counts?.send_failed ?? 0) > 0;
  });

/** Число договоров колонки по счётчикам статусов. */
export const contractColumnCount = (
  counts: Readonly<Record<string, number>>,
  statuses: ReadonlyArray<string>,
): number => statuses.reduce((acc, status) => acc + (counts[status] ?? 0), 0);

/** Тон колонки — тон её первого статуса (как у плашки статуса). */
export const contractColumnTone = (
  column: Pick<IContractBoardColumn, 'key'>,
): EsignTagTone => {
  if (column.key === 'closed' || column.key === 'failed') return 'danger';
  if (column.key === 'draft') return 'neutral';
  const base = CONTRACT_BOARD_COLUMNS.find((item) => item.key === column.key);
  return esignStatusTone(base ? base.statuses[base.statuses.length - 1] : '');
};

interface IContractTimes {
  status: string;
  createdAt?: string | null;
  sentAt?: string | null;
}

/**
 * Сколько дней договор ждёт: отправленный и открытый — с отправки, черновик —
 * с создания; подписанный и закрытый не ждут (`null`).
 */
export const contractWaitingDays = (
  row: IContractTimes,
  now: moment.Moment = moment(),
): number | null => {
  const waiting = ['draft', 'send_failed', 'sending', 'sent', 'opened'];
  if (!waiting.includes(row.status)) return null;
  const from = ['sent', 'opened'].includes(row.status)
    ? row.sentAt || row.createdAt
    : row.createdAt;
  if (!from || !moment(from).isValid()) return null;
  return Math.max(0, now.diff(moment(from), 'days'));
};

/** Плитки-итоги над «Договорами» — по счётчикам статусов под фильтрами. */
export interface IContractsSummary {
  awaiting: number;
  signed: number;
  drafts: number;
  lost: number;
}

export const contractsSummary = (
  counts: Readonly<Record<string, number>>,
): IContractsSummary => ({
  awaiting: contractColumnCount(counts, ['sending', 'sent', 'opened']),
  signed: contractColumnCount(counts, ['completed']),
  drafts: contractColumnCount(counts, ['draft', 'send_failed']),
  lost: contractColumnCount(counts, ['rejected', 'expired', 'cancelled']),
});

/* ── ТЗ 0106: подписанные за 30 дней ─────────────────────────────────── */

/** Окно плиток «Подписано за 30 дней» и «Сумма подписанных за 30 дней». */
export const CONTRACTS_SIGNED_RECENT_DAYS = 30;

/**
 * Переменные `ContractSignedRecently`: прочие фильтры списка без статуса
 * (статус — всегда `completed`, его ставит операция) и `signedFrom` —
 * «сегодня по Варшаве минус 30 дней». `today` — для тестов (`YYYY-MM-DD`).
 */
export const toContractsSignedRecentVariables = <
  T extends { status?: string | null }
>(
  filters: T,
  today?: string,
): Omit<T, 'status'> & { signedFrom: string } => {
  const rest: Partial<T> = { ...filters };
  delete rest.status;
  return {
    ...(rest as Omit<T, 'status'>),
    // «За 30 дней» включая сегодня: сегодня и 29 предыдущих дней.
    signedFrom: warsawDaysAgoIso(CONTRACTS_SIGNED_RECENT_DAYS - 1, today),
  };
};

export interface IContractAmount {
  currency: string;
  amount: unknown;
  count: number;
}

/**
 * Суммы по валютам для плитки: без нулевых, PLN первой, дальше по коду
 * валюты. Валюты не складываются — каждая своей строкой.
 */
export const contractAmountRows = <T extends IContractAmount>(
  rows: ReadonlyArray<T>,
): T[] =>
  rows
    .filter(
      (row) => Number(row.amount) !== 0 && Number.isFinite(Number(row.amount)),
    )
    .sort((a, b) => {
      if (a.currency === b.currency) return 0;
      if (a.currency === 'PLN') return -1;
      if (b.currency === 'PLN') return 1;
      return a.currency < b.currency ? -1 : 1;
    });

/* ── ТЗ 0120, этап 2: доска как CRM ──────────────────────────────────── */

/** «Горячих» в колонке — сумма `hotCount` её статусов. */
export const contractColumnHot = (
  hotCounts: Readonly<Record<string, number>>,
  statuses: ReadonlyArray<string>,
): number =>
  statuses.reduce((acc, status) => acc + (hotCounts[status] ?? 0), 0);

interface IPricedRow {
  price?: unknown;
  currency?: string | null;
}

/**
 * Сумма колонки по валютам — по загруженным карточкам (PLN первой). Валюты
 * не складываются; пустая и нулевая цена не в счёт.
 */
export const contractColumnAmounts = (
  rows: ReadonlyArray<IPricedRow>,
): Array<{ currency: string; amount: number; count: number }> => {
  const byCurrency = new Map<string, { amount: number; count: number }>();
  rows.forEach((row) => {
    const value = Number(row.price);
    if (row.price === null || row.price === undefined) return;
    if (!Number.isFinite(value) || value === 0) return;
    const currency = row.currency || 'PLN';
    const prev = byCurrency.get(currency) ?? { amount: 0, count: 0 };
    byCurrency.set(currency, {
      amount: prev.amount + value,
      count: prev.count + 1,
    });
  });
  return contractAmountRows(
    Array.from(byCurrency.entries()).map(([currency, item]) => ({
      currency,
      ...item,
    })),
  );
};

/** Тон плашек CRM-слоя договора (как `tagToneColors`). */
export type ContractCrmTone = 'neutral' | 'info' | 'ok' | 'warn' | 'danger';

/** «Открыл — не подписал» дольше — красным. */
export const CONTRACT_OPENED_STALE_DAYS = 3;

export interface IContractSignStateView {
  /** Ключ подписи: `esign.signState.<key>`. */
  key: 'not_opened' | 'clicked' | 'opened' | 'signed';
  tone: ContractCrmTone;
  /** Сколько дней открыт и не подписан (только `opened`). */
  days: number | null;
}

interface ISignStateRow {
  signState?: string | null;
  openedAt?: string | null;
}

/**
 * Состояние подписи словами и цветом (`signState` сервера): не открыл
 * (нейтрально), кликнул ссылку (инфо), открыл — не подписал (янтарь; с
 * `openedAt` три дня и больше — красный), подписан (зелёный). Черновик и
 * закрытый — `null`: про них говорит статус.
 */
export const contractSignStateView = (
  row: ISignStateRow,
  now: moment.Moment = moment(),
): IContractSignStateView | null => {
  switch (row.signState) {
    case 'not_opened':
      return { key: 'not_opened', tone: 'neutral', days: null };
    case 'clicked':
      return { key: 'clicked', tone: 'info', days: null };
    case 'signed':
      return { key: 'signed', tone: 'ok', days: null };
    case 'opened': {
      const at = row.openedAt ? moment(row.openedAt) : null;
      const days =
        at && at.isValid() ? Math.max(0, now.diff(at, 'days')) : null;
      return {
        key: 'opened',
        tone:
          days !== null && days >= CONTRACT_OPENED_STALE_DAYS
            ? 'danger'
            : 'warn',
        days,
      };
    }
    default:
      return null;
  }
};

/** Плашка «истекает» на карточке — только когда до срока ≤ 7 дней. */
export const CONTRACT_EXPIRY_SHOWN_DAYS = 7;

/** Тон срока ссылки: ≤ 1 дн. — красный, ≤ 3 — янтарь, дальше — нейтрально. */
export const contractExpiryTone = (
  days: number | null | undefined,
): ContractCrmTone | null => {
  if (days === null || days === undefined) return null;
  if (days <= 1) return 'danger';
  if (days <= 3) return 'warn';
  return 'neutral';
};
